import React, { useEffect, useState } from 'react';
import { Button, Input, Combobox, Modal, Table, Badge } from '../../components/common';
import { userService, userGroupService } from '../../services/resources';
import { apiError } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { sortByLabel } from '../../config/columns';

export default function UsersTab({ onToast }) {
  const { can } = useAuth();
  const canCreate = can('SETTINGS', 'CanCreate');
  const canEdit = can('SETTINGS', 'CanEdit');
  const canDelete = can('SETTINGS', 'CanDelete');

  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ UserName: '', Password: '', GroupID: '', IsActive: true });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, g] = await Promise.all([userService.list(), userGroupService.list()]);
      setRows(u);
      setGroups(g);
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const groupOptions = sortByLabel(groups.map((g) => ({ value: g.GroupID, label: g.GroupName })));

  const openCreate = () => {
    setEditing(null);
    setForm({ UserName: '', Password: '', GroupID: '', IsActive: true });
    setError('');
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ UserName: row.UserName, Password: '', GroupID: row.GroupID, IsActive: !!row.IsActive });
    setError('');
    setOpen(true);
  };

  const toggleBlock = async (row) => {
    try {
      if (row.IsActive) {
        await userService.block(row.UserID);
        onToast?.('User blocked');
      } else {
        await userService.unblock(row.UserID);
        onToast?.('User unblocked');
      }
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  const removeUser = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete user "${row.UserName}"? This cannot be undone.`)) return;
    try {
      await userService.remove(row.UserID);
      onToast?.('User deleted');
      load();
    } catch (err) {
      onToast?.(apiError(err).message, 'error');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        GroupID: Number(form.GroupID),
        IsActive: form.IsActive,
        Password: form.Password || undefined,
      };
      if (editing) {
        await userService.update(editing.UserID, payload);
        onToast?.('User updated');
      } else {
        await userService.create({ ...payload, UserName: form.UserName, Password: form.Password });
        onToast?.('User created');
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(apiError(err).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {canCreate && (
        <div className="mb-3 flex justify-end">
          <Button onClick={openCreate}>+ New User</Button>
        </div>
      )}
      <Table
        loading={loading}
        rows={rows}
        columns={[
          { key: 'UserName', label: 'Username' },
          { key: 'GroupName', label: 'Group' },
          {
            key: 'IsActive',
            label: 'Status',
            render: (r) =>
              r.IsActive ? <Badge tone="green">Active</Badge> : <Badge tone="gray">Disabled</Badge>,
          },
          {
            key: 'LastLogin',
            label: 'Last Login',
            render: (r) => (r.LastLogin ? new Date(r.LastLogin).toLocaleString() : '—'),
          },
          {
            key: '__a',
            label: 'Actions',
            className: 'text-right',
            render: (r) => (
              <div className="flex justify-end gap-1.5">
                {canEdit && (
                  <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>
                    Edit
                  </Button>
                )}
                {canEdit && (
                  <Button
                    size="sm"
                    variant={r.IsActive ? 'ghost' : 'secondary'}
                    onClick={() => toggleBlock(r)}
                  >
                    {r.IsActive ? 'Block' : 'Unblock'}
                  </Button>
                )}
                {canDelete && (
                  <Button size="sm" variant="danger" onClick={() => removeUser(r)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? `Edit ${editing.UserName}` : 'New User'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-3">
          {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
          <Input
            label="Username"
            required
            value={form.UserName}
            disabled={!!editing}
            onChange={(e) => setForm({ ...form, UserName: e.target.value })}
          />
          <Input
            label={editing ? 'Password (leave blank to keep current)' : 'Password'}
            type="password"
            required={!editing}
            value={form.Password}
            placeholder={editing ? '••••••••' : ''}
            onChange={(e) => setForm({ ...form, Password: e.target.value })}
          />
          <Combobox
            label="Group"
            required
            placeholder="-- select group --"
            options={groupOptions}
            value={form.GroupID}
            onChange={(v) => setForm({ ...form, GroupID: v })}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.IsActive}
              onChange={(e) => setForm({ ...form, IsActive: e.target.checked })}
            />
            Active
          </label>
        </form>
      </Modal>
    </div>
  );
}
